// DashboardPage.duck.js
import pick from 'lodash/pick';
import Decimal from 'decimal.js';
import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';
import moment from 'moment';

import { fetchCurrentUser } from '../../ducks/user.duck';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  createRecipientApi,
  destroyRecipientApi,
  retrieveRecipientApi,
  earningsSummaryApi,
} from '../../util/api';
import { storableError } from '../../util/errors';
import { parse } from '../../util/urlHelpers';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { getSubMerchantIdFromUser } from '../../util/genericHelpers';
import {
  buildInvalidPayoutDetailsSnapshot,
  buildPayoutDetailsSnapshot,
} from '../../util/payoutDetails';

// ================ Action types ================ //

// Existing (kept) – initial flags for this duck slice
export const SET_INITIAL_VALUES = 'app/DashboardPage/SET_INITIAL_VALUES';

// Recipient CRUD
export const CREATE_OMISE_RECIPIENT_REQUEST = 'app/DashboardPage/CREATE_OMISE_RECIPIENT_REQUEST';
export const CREATE_OMISE_RECIPIENT_SUCCESS = 'app/DashboardPage/CREATE_OMISE_RECIPIENT_SUCCESS';
export const CREATE_OMISE_RECIPIENT_ERROR = 'app/DashboardPage/CREATE_OMISE_RECIPIENT_ERROR';

export const SET_OMISE_RECIPIENT_DATA_REQUEST =
  'app/DashboardPage/SET_OMISE_RECIPIENT_DATA_REQUEST';
export const SET_OMISE_RECIPIENT_DATA_SUCCESS =
  'app/DashboardPage/SET_OMISE_RECIPIENT_DATA_SUCCESS';
export const SET_OMISE_RECIPIENT_DATA_ERROR = 'app/DashboardPage/SET_OMISE_RECIPIENT_DATA_ERROR';
export const SET_OMISE_RECIPIENT_NOT_FOUND = 'app/DashboardPage/SET_OMISE_RECIPIENT_NOT_FOUND';

export const DESTROY_OMISE_RECIPIENT_REQUEST = 'app/DashboardPage/DESTROY_OMISE_RECIPIENT_REQUEST';
export const DESTROY_OMISE_RECIPIENT_SUCCESS = 'app/DashboardPage/DESTROY_OMISE_RECIPIENT_SUCCESS';
export const DESTROY_OMISE_RECIPIENT_ERROR = 'app/DashboardPage/DESTROY_OMISE_RECIPIENT_ERROR';

// Earnings summary
export const FETCH_EARNINGS_SUMMARY_REQUEST = 'app/DashboardPage/FETCH_EARNINGS_SUMMARY_REQUEST';
export const FETCH_EARNINGS_SUMMARY_SUCCESS = 'app/DashboardPage/FETCH_EARNINGS_SUMMARY_SUCCESS';
export const FETCH_EARNINGS_SUMMARY_ERROR = 'app/DashboardPage/FETCH_EARNINGS_SUMMARY_ERROR';

// Dashboard transactions
export const FETCH_DASHBOARD_TRANSACTIONS_REQUEST =
  'app/DashboardPage/FETCH_DASHBOARD_TRANSACTIONS_REQUEST';
export const FETCH_DASHBOARD_TRANSACTIONS_SUCCESS =
  'app/DashboardPage/FETCH_DASHBOARD_TRANSACTIONS_SUCCESS';
export const FETCH_DASHBOARD_TRANSACTIONS_ERROR =
  'app/DashboardPage/FETCH_DASHBOARD_TRANSACTIONS_ERROR';

// ================ Initial state ================ //

const isOlderThan14Days = date => {
  return moment().diff(moment(date), 'days') > 14;
};

const sortedTransactions = txs =>
  reverse(
    sortBy(txs || [], tx => {
      return tx.attributes ? tx.attributes.lastTransitionedAt : null;
    })
  );

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const filterDashboardTransactions = transactions => {
  return sortedTransactions(transactions).filter(tx => {
    const lastTransition = tx.attributes?.lastTransition;
    const lastTransitionedAt = tx.attributes?.lastTransitionedAt;

    if (!lastTransitionedAt) return true;

    const isInquiry = lastTransition === 'transition/inquire';
    if (isInquiry && isOlderThan14Days(lastTransitionedAt)) {
      return false;
    }

    const isPaymentFailed =
      lastTransition === 'transition/payment-failed' ||
      lastTransition === 'transition/expire-payment';
    if (isPaymentFailed && isOlderThan14Days(lastTransitionedAt)) {
      return false;
    }

    return true;
  });
};

const initialState = {
  // payout details (recipient)
  payoutDetailsSaveInProgress: false,
  payoutDetailsSaved: false,
  fromReturnURL: false,

  omiseRecipientInProgress: false,
  omiseRecipientCreated: false,
  omiseRecipientError: null,

  omiseRecipientData: null,
  omiseRecipientDataInProgress: false, // (keep the misspelling to avoid regressions)
  omiseRecipientDataError: null,
  omiseRecipientNotFound: false,
  staleOmiseRecipientId: null,
  payoutStatus: 'not_connected',
  canReceivePayouts: false,
  requiresPayoutSetup: true,

  // balances
  summaryInProgress: false,
  summaryError: null,
  summary: null, // { totalEarnings, onHold, etc. }

  transactionsInProgress: false,
  transactionsError: null,
  transactionsPagination: null,
  transactionRefs: [],
};

// ================ Reducer ================ //

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    // Recipient data load
    case SET_OMISE_RECIPIENT_DATA_REQUEST:
      return {
        ...state,
        omiseRecipientDataInProgress: true,
        omiseRecipientDataError: null,
        omiseRecipientNotFound: false,
        staleOmiseRecipientId: null,
        payoutStatus: 'loading',
        canReceivePayouts: false,
        requiresPayoutSetup: true,
      };
    case SET_OMISE_RECIPIENT_DATA_SUCCESS:
      return {
        ...state,
        omiseRecipientData: payload || null,
        omiseRecipientDataInProgress: false,
        omiseRecipientDataError: null,
        omiseRecipientNotFound: false,
        staleOmiseRecipientId: null,
        payoutStatus: payload ? 'recipient_connected' : 'not_connected',
        canReceivePayouts: !!payload,
        requiresPayoutSetup: !payload,
      };
    case SET_OMISE_RECIPIENT_DATA_ERROR:
      return {
        ...state,
        omiseRecipientDataError: payload,
        omiseRecipientDataInProgress: false,
        payoutStatus: 'verification_failed',
        canReceivePayouts: false,
        requiresPayoutSetup: false,
      };
    case SET_OMISE_RECIPIENT_NOT_FOUND:
      return {
        ...state,
        omiseRecipientData: null,
        omiseRecipientDataInProgress: false,
        omiseRecipientDataError: null,
        omiseRecipientNotFound: true,
        staleOmiseRecipientId: payload?.recipientId || null,
        payoutStatus: payload?.payoutStatus || 'recipient_missing',
        canReceivePayouts: false,
        requiresPayoutSetup: true,
      };

    // Recipient create/update
    case CREATE_OMISE_RECIPIENT_REQUEST:
      return {
        ...state,
        omiseRecipientInProgress: true,
        omiseRecipientCreated: false,
        omiseRecipientError: null,
      };
    case CREATE_OMISE_RECIPIENT_SUCCESS:
      return { ...state, omiseRecipientInProgress: false, omiseRecipientCreated: true };
    case CREATE_OMISE_RECIPIENT_ERROR:
      return { ...state, omiseRecipientInProgress: false, omiseRecipientError: payload };

    // Recipient destroy
    case DESTROY_OMISE_RECIPIENT_REQUEST:
      return { ...state, omiseRecipientInProgress: true, omiseRecipientError: null };
    case DESTROY_OMISE_RECIPIENT_SUCCESS:
      return {
        ...state,
        omiseRecipientInProgress: false,
        omiseRecipientCreated: false,
        omiseRecipientData: null,
      };
    case DESTROY_OMISE_RECIPIENT_ERROR:
      return { ...state, omiseRecipientInProgress: false, omiseRecipientError: payload };

    // Summary
    case FETCH_EARNINGS_SUMMARY_REQUEST:
      return { ...state, summaryInProgress: true, summaryError: null };
    case FETCH_EARNINGS_SUMMARY_SUCCESS:
      return { ...state, summaryInProgress: false, summary: payload, summaryError: null };
    case FETCH_EARNINGS_SUMMARY_ERROR:
      return { ...state, summaryInProgress: false, summaryError: payload };

    // Dashboard transactions
    case FETCH_DASHBOARD_TRANSACTIONS_REQUEST:
      return { ...state, transactionsInProgress: true, transactionsError: null };
    case FETCH_DASHBOARD_TRANSACTIONS_SUCCESS: {
      const response = payload?.response;
      const filteredTransactions = filterDashboardTransactions(response?.data?.data || []);
      return {
        ...state,
        transactionsInProgress: false,
        transactionRefs: entityRefs(filteredTransactions),
        transactionsPagination: response?.data?.meta || null,
        transactionsError: null,
      };
    }
    case FETCH_DASHBOARD_TRANSACTIONS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, transactionsInProgress: false, transactionsError: payload };

    default:
      return state;
  }
}

// ================ Selectors & helpers ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

const selectRecipientId = state =>
  state?.user?.currentUser?.attributes?.profile?.publicData?.omiseRecipientId;

const markStoredRecipientInvalid = async ({ dispatch, currentUser, recipientId, reason }) => {
  const existingPrivateData = currentUser?.attributes?.profile?.privateData || {};
  const payoutDetails = buildInvalidPayoutDetailsSnapshot(currentUser, { recipientId, reason });

  await dispatch(
    updateProfile({
      publicData: { omiseRecipientId: null },
      privateData: { ...existingPrivateData, payoutDetails },
    })
  );
};

// ================ Action creators ================ //

export const createOmiseRecipientRequest = () => ({ type: CREATE_OMISE_RECIPIENT_REQUEST });
export const createOmiseRecipientSuccess = () => ({ type: CREATE_OMISE_RECIPIENT_SUCCESS });
export const createOmiseRecipientError = e => ({ type: CREATE_OMISE_RECIPIENT_ERROR, payload: e });

export const setOmiseRecipientDataRequest = () => ({ type: SET_OMISE_RECIPIENT_DATA_REQUEST });
export const setOmiseRecipientDataSuccess = recipient => ({
  type: SET_OMISE_RECIPIENT_DATA_SUCCESS,
  payload: recipient,
});
export const setOmiseRecipientDataError = e => ({
  type: SET_OMISE_RECIPIENT_DATA_ERROR,
  payload: e,
});
export const setOmiseRecipientNotFound = payload => ({
  type: SET_OMISE_RECIPIENT_NOT_FOUND,
  payload,
});

export const destroyOmiseRecipientRequest = () => ({ type: DESTROY_OMISE_RECIPIENT_REQUEST });
export const destroyOmiseRecipientSuccess = () => ({ type: DESTROY_OMISE_RECIPIENT_SUCCESS });
export const destroyOmiseRecipientError = e => ({
  type: DESTROY_OMISE_RECIPIENT_ERROR,
  payload: e,
});

const fetchSummaryRequest = () => ({ type: FETCH_EARNINGS_SUMMARY_REQUEST });
const fetchSummarySuccess = data => ({ type: FETCH_EARNINGS_SUMMARY_SUCCESS, payload: data });
const fetchSummaryError = e => ({ type: FETCH_EARNINGS_SUMMARY_ERROR, payload: e });

const fetchDashboardTransactionsRequest = () => ({
  type: FETCH_DASHBOARD_TRANSACTIONS_REQUEST,
});
const fetchDashboardTransactionsSuccess = response => ({
  type: FETCH_DASHBOARD_TRANSACTIONS_SUCCESS,
  payload: { response },
});
const fetchDashboardTransactionsError = e => ({
  type: FETCH_DASHBOARD_TRANSACTIONS_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

const DASHBOARD_TRANSACTIONS_PAGE_SIZE = 100;

const parseSearchParams = search => {
  if (!search) return {};
  if (typeof search === 'string') return parse(search);
  if (typeof search === 'object') return search;
  return {};
};

// Create or update recipient; then persist id to publicData
export const createOmiseRecipientForRefund = valuesArg => async (dispatch, getState) => {
  dispatch(createOmiseRecipientRequest());

  const state = getState();
  const existingRecipientId = selectRecipientId(state) || null;

  const values = valuesArg?.values ? valuesArg.values : valuesArg || {};
  const requestPayload = { ...values, recipientId: existingRecipientId };

  try {
    const response = await createRecipientApi(requestPayload);
    const newRecipient = response?.recipient || null;
    const newId = newRecipient?.id;

    if (newRecipient) dispatch(setOmiseRecipientDataSuccess(newRecipient));

    const currentUser = state.user?.currentUser;
    const existingPublicData = currentUser?.attributes?.profile?.publicData || {};
    const existingPrivateData = currentUser?.attributes?.profile?.privateData || {};
    const recipientIdToStore = newId || existingRecipientId;
    const payoutDetailsSnapshot = buildPayoutDetailsSnapshot(values);

    if (recipientIdToStore) {
      payoutDetailsSnapshot.recipientId = recipientIdToStore;
    }

    const nextPublicData = recipientIdToStore
      ? { ...existingPublicData, omiseRecipientId: recipientIdToStore }
      : existingPublicData;
    const nextPrivateData = {
      ...existingPrivateData,
      payoutDetails: payoutDetailsSnapshot,
    };

    await dispatch(updateProfile({ publicData: nextPublicData, privateData: nextPrivateData }));
    await dispatch(fetchCurrentUser());

    dispatch(createOmiseRecipientSuccess());
    return newId;
  } catch (error) {
    console.error('Omise recipient creation/update failed:', error);
    dispatch(createOmiseRecipientError(error));
    throw error;
  }
};

// Retrieve recipient (if an id exists on the profile)
export const retrieveOmiseRecipient = () => async (dispatch, getState) => {
  const recipientId = selectRecipientId(getState());
  if (!recipientId) return null;

  dispatch(setOmiseRecipientDataRequest());
  try {
    const subMerchantId = getSubMerchantIdFromUser(getState().user.currentUser);
    const response = await retrieveRecipientApi({ recipientId, subMerchantId });
    if (response?.recipientNotFound) {
      const currentUser = getState().user.currentUser;
      const reason = response?.reason || 'OMISE_RECIPIENT_NOT_FOUND';
      dispatch(
        setOmiseRecipientNotFound({
          recipientId,
          reason,
          payoutStatus: response?.payoutStatus,
        })
      );
      try {
        await markStoredRecipientInvalid({ dispatch, currentUser, recipientId, reason });
        await dispatch(fetchCurrentUser({ updateHasListings: false, updateNotifications: false }));
      } catch (profileError) {
        console.warn('Failed to mark stale Omise recipient invalid:', profileError);
      }
      return null;
    }

    const recipient = response?.recipient || null;
    dispatch(setOmiseRecipientDataSuccess(recipient));
    return recipient;
  } catch (error) {
    dispatch(setOmiseRecipientDataError(error));
    return null;
  }
};

export const destroyOmiseRecipient = recipientId => async dispatch => {
  dispatch(destroyOmiseRecipientRequest());
  try {
    await destroyRecipientApi({ recipientId });
    await dispatch(updateProfile({ publicData: { omiseRecipientId: null } }));
    await dispatch(fetchCurrentUser());
    dispatch(setOmiseRecipientDataSuccess(null));
    dispatch(destroyOmiseRecipientSuccess());
    return true;
  } catch (error) {
    console.error('Destroy Omise recipient failed:', error);
    dispatch(destroyOmiseRecipientError(error));
    throw error;
  }
};

// Earnings summary
export const fetchEarningsSummary = () => async dispatch => {
  dispatch(fetchSummaryRequest());
  try {
    const data = await earningsSummaryApi();
    // Normalize numbers to strings with currency
    const fmt = v => (typeof v === 'number' ? `฿${new Decimal(v).toFixed(2)}` : v);
    const payload = {
      totalEarnings: fmt(data?.totalEarnings),
      onHold: fmt(data?.onHold),
      // raw/minor (cents) for enable/disable actions if needed
      totalEarningsMinor: data?.totalEarningsMinor || 0,
      onHoldMinor: data?.onHoldMinor || 0,
      currency: 'THB',
      recipient: data?.recipient || null,
      payoutStatus: data?.payoutStatus || null,
      canReceivePayouts: !!data?.canReceivePayouts,
      requiresPayoutSetup: !!data?.requiresPayoutSetup,
    };
    dispatch(fetchSummarySuccess(payload));
    return payload;
  } catch (e) {
    dispatch(fetchSummaryError(e));
    throw e;
  }
};

export const loadDashboardTransactions = (params = { tab: 'sales' }, search = '?page=1') => async (
  dispatch,
  _getState,
  sdk
) => {
  const { tab = 'sales' } = params || {};
  const onlyFilter = tab === 'sales' ? 'sale' : tab === 'orders' ? 'order' : null;

  if (!onlyFilter) {
    return Promise.reject(new Error(`Invalid tab for DashboardPage: ${tab}`));
  }

  dispatch(fetchDashboardTransactionsRequest());

  try {
    const { page = 1 } = parseSearchParams(search);
    const initialPage = Number.isFinite(Number(page)) && Number(page) > 0 ? Number(page) : 1;

    const queryParams = {
      only: onlyFilter,
      include: [
        'listing',
        'provider',
        'listing.images',
        'provider.profileImage',
        'customer',
        'customer.profileImage',
        'booking',
        'reviews',
      ],
      perPage: DASHBOARD_TRANSACTIONS_PAGE_SIZE,
      sort: '-createdAt',
      page: initialPage,
      'fields.transaction': [
        'createdAt',
        'lastTransition',
        'lastTransitionedAt',
        'transitions',
        'metadata',
        'protectedData',
        'lineItems',
        'payinTotal',
        'payoutTotal',
        'processName',
        'listing',
        'provider',
        'customer',
        'booking',
        'reviews',
      ],
      'fields.listing': ['title', 'publicData', 'availabilityPlan', 'images'],
      'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'profileImage'],
      'fields.booking': ['displayStart', 'displayEnd', 'end'],
      'fields.review': ['rating', 'content', 'createdAt', 'author'],
      'fields.image': ['variants.default'],
      'limit.images': 1,
      'imageVariant.default': 'w:160;h:120;fit:crop',
    };
    const response = await sdk.transactions.query(queryParams);
    dispatch(addMarketplaceEntities(response));
    dispatch(fetchDashboardTransactionsSuccess(response));
    return response;
  } catch (e) {
    dispatch(fetchDashboardTransactionsError(storableError(e)));
    throw e;
  }
};

// Load user + recipient + dashboard data
export const loadData = () => async (dispatch, getState) => {
  dispatch(setInitialValues());
  const fetchCurrentUserOptions = { updateHasListings: false, updateNotifications: false };
  await dispatch(fetchCurrentUser(fetchCurrentUserOptions));

  const recipientId = selectRecipientId(getState());
  if (recipientId) {
    dispatch(retrieveOmiseRecipient()).catch(() => null);
  }

  return true;
};
